import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'
import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/integrations/supabase/types'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { NDAModal } from '@/components/business/NDAModal'
import { MapPin, DollarSign, TrendingUp, Building, Calendar, Users, Briefcase, Clock, FileText, ArrowLeft, Heart } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { leadFormSchema, checkRateLimit, getRateLimitResetTime } from '@/utils/formValidation'
import { ZodError } from 'zod'
import { useAnalytics } from '@/hooks/useAnalytics'
import { getListingImage } from '@/utils/getListingImage'
import { Helmet } from 'react-helmet-async'

type Listing = Database['public']['Views']['v_listings_with_image']['Row'] & {
  price: number | null
  requires_nda?: boolean
}

const BusinessDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const fromSwipe = location.state?.fromSwipe
  const [listing, setListing] = useState<Listing | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [showNDAModal, setShowNDAModal] = useState(false)
  const [hasSignedNDA, setHasSignedNDA] = useState(false)
  const [checkingNDA, setCheckingNDA] = useState(false)
  const [isFavorited, setIsFavorited] = useState(false)
  const [isOwner, setIsOwner] = useState(false)
  const { toast } = useToast()
  const { trackListingView, trackLeadSubmitted, trackNDASign } = useAnalytics()

  // Check authentication status
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)
    }
    checkUser()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (id) {
      fetchListing()
    }
  }, [id])

  useEffect(() => {
    if (user && listing) {
      checkFavoriteStatus()
      setIsOwner(listing.broker_id === user.id)
    }
  }, [user, listing])

  const fetchListing = async () => {
    try {
      const { data, error } = await supabase
        .from('v_listings_with_image')
        .select('*')
        .eq('id', id)
        .eq('status', 'live')
        .single()

      if (error) throw error
      setListing(data)
      
      // Track listing view
      trackListingView(data.id, data.title || 'Untitled Listing')
      
      // Check if this listing requires NDA
      if (data.requires_nda) {
        checkNDAStatus()
      }
    } catch (error) {
      console.error('Error fetching listing:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkFavoriteStatus = async () => {
    if (!user || !id) return
    
    try {
      const { data } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('listing_id', id)
        .single()
      
      setIsFavorited(!!data)
    } catch (error) {
      // Not favorited
    }
  }

  const toggleFavorite = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save favorites",
        variant: "destructive"
      })
      return
    }

    try {
      if (isFavorited) {
        await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('listing_id', id)
        
        setIsFavorited(false)
        toast({ title: "Removed from favorites" })
      } else {
        await supabase
          .from('favorites')
          .insert({ user_id: user.id, listing_id: id })
        
        setIsFavorited(true)
        toast({ title: "Added to favorites!" })
      }
    } catch (error) {
      console.error('Error toggling favorite:', error)
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive"
      })
    }
  }

  const checkNDAStatus = async () => {
    // For now, we'll assume NDA is not signed and require them to sign each time
    // In a real app, you might want to store this in localStorage or session storage
    setHasSignedNDA(false)
  }

  const handleExpressInterest = () => {
    // Check if user is authenticated
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to express interest in this listing.',
        variant: 'destructive',
      })
      navigate('/sign-in?redirect=' + encodeURIComponent(window.location.pathname))
      return
    }

    if (listing?.requires_nda && !hasSignedNDA) {
      setShowNDAModal(true)
    } else {
      setIsDialogOpen(true)
    }
  }

  const handleNDAAccepted = () => {
    setHasSignedNDA(true)
    setIsDialogOpen(true)
  }

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return
    setErrors({})

    // Double-check authentication
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to express interest.',
        variant: 'destructive',
      })
      navigate('/sign-in?redirect=' + encodeURIComponent(window.location.pathname))
      return
    }

    // Check rate limit (3 leads per hour per user)
    const rateLimitKey = `lead-${user.id}-${id}`
    if (!checkRateLimit(rateLimitKey, 3, 3600000)) {
      const resetTime = getRateLimitResetTime(rateLimitKey)
      const minutesLeft = resetTime ? Math.ceil(resetTime / 60000) : 60
      toast({
        title: "Too Many Requests",
        description: `Please wait ${minutesLeft} minutes before submitting another interest request.`,
        variant: "destructive"
      })
      return
    }

    // Validate form data
    try {
      const validatedData = leadFormSchema.parse(leadForm)
      setIsSubmitting(true)

      // Send lead notification email (fire-and-forget)
      try {
        await supabase.functions.invoke('send-lead-email', {
          body: {
            buyerName: validatedData.name,
            buyerEmail: validatedData.email,
            buyerPhone: validatedData.phone || null,
            message: validatedData.message || null,
            listingTitle: listing?.title || 'Business Listing',
            listingId: id
          }
        })
      } catch (emailError) {
        console.error('Email notification error:', emailError)
        // Continue even if email fails - we'll still try to create the lead
      }

      // Insert the lead with validated data
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .insert({
          listing_id: id,
          user_id: user.id,
          buyer_name: validatedData.name,
          buyer_email: validatedData.email,
          buyer_phone: validatedData.phone || null,
          message: validatedData.message || null,
          source: 'web',
        })
        .select('id')
        .single()

      if (leadError) throw leadError

      // Track lead submission
      trackLeadSubmitted(id || '', 'express_interest')


      // Then, process the lead payment
      try {
        const { data: paymentResult, error: paymentError } = await supabase.functions.invoke('create-lead-payment', {
          body: { lead_id: leadData.id }
        })

        if (paymentError) {
          console.error('Payment processing error:', paymentError)
          // Lead was created but payment failed - this is okay, payment can be retried
        } else {
          console.log('Lead payment processed:', paymentResult)
        }
      } catch (paymentError) {
        console.error('Payment function error:', paymentError)
        // Lead was created but payment failed - this is okay, payment can be retried
      }

      toast({
        title: 'Interest Submitted',
        description: 'Your interest has been submitted. The broker will contact you shortly.',
      })

      setLeadForm({ name: '', email: '', phone: '', message: '' })
      setIsDialogOpen(false)
    } catch (error) {
      if (error instanceof ZodError) {
        // Handle validation errors
        const fieldErrors: Record<string, string> = {}
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message
          }
        })
        setErrors(fieldErrors)
        toast({
          title: "Validation Error",
          description: "Please check the form for errors.",
          variant: "destructive"
        })
      } else {
        console.error('Error submitting lead:', error)
        toast({
          title: 'Error',
          description: 'Failed to submit your interest. Please try again.',
          variant: 'destructive',
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatPrice = (min: number | null, max: number | null) => {
    if (!min && !max) return 'Price on Application'
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()}`
    if (min) return `From $${min.toLocaleString()}`
    if (max) return `Up to $${max.toLocaleString()}`
    return 'Price on Application'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading business details...</p>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Business not found.</p>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background" data-prerender-ready={listing ? "true" : undefined}>
      <Helmet>
        <title>{`${listing.title} - ${listing.sector} Business for Sale | BizMatch`}</title>
        <link rel="canonical" href={`https://www.bizmatch.com.au/business/${listing.id}/`} />
        <meta name="description" content={(listing.teaser || listing.description || '').substring(0, 160)} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://www.bizmatch.com.au/business/${listing.id}/`} />
        <meta property="og:title" content={`${listing.title} - ${listing.sector} Business for Sale | BizMatch`} />
        <meta property="og:description" content={(listing.teaser || listing.description || '').substring(0, 160)} />
        <meta property="og:image" content={(listing as any).image_url || 'https://www.bizmatch.com.au/bizmatch-logo-transparent.png'} />
        <meta property="og:site_name" content="BizMatch" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://www.bizmatch.com.au/business/${listing.id}/`} />
        <meta name="twitter:title" content={`${listing.title} - ${listing.sector} Business for Sale`} />
        <meta name="twitter:description" content={(listing.teaser || listing.description || '').substring(0, 160)} />
        <meta name="twitter:image" content={(listing as any).image_url || 'https://www.bizmatch.com.au/bizmatch-logo-transparent.png'} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: listing.title,
          description: listing.description || listing.teaser || undefined,
          url: `https://www.bizmatch.com.au/business/${listing.id}/`,
          image: (listing as any).image_url ? [(listing as any).image_url] : [],
          address: {
            '@type': 'PostalAddress',
            addressLocality: listing.location_suburb || undefined,
            addressRegion: listing.location_state || undefined,
            addressCountry: 'AU'
          },
          ...(listing.price && {
            offers: {
              '@type': 'Offer',
              priceCurrency: 'AUD',
              price: listing.price,
              availability: 'https://schema.org/InStock',
              url: `https://www.bizmatch.com.au/business/${listing.id}/`
            }
          })
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bizmatch.com.au/' },
            { '@type': 'ListItem', position: 2, name: 'Explore', item: 'https://www.bizmatch.com.au/explore/' },
            { '@type': 'ListItem', position: 3, name: listing.title, item: `https://www.bizmatch.com.au/business/${listing.id}/` }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-4 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="outline" 
            onClick={() => navigate(fromSwipe ? '/swipe' : '/explore')}
            className="mb-6 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {fromSwipe ? 'Back to Swipe Mode' : 'Back to Listings'}
          </Button>

          {/* Hero Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  {listing.listing_number && (
                    <Badge variant="outline" className="font-mono">#{listing.listing_number}</Badge>
                  )}
                  <Badge variant="secondary">{listing.sector}</Badge>
                  {listing.requires_nda && (
                    <Badge variant="outline" className="text-orange-600 border-orange-200">
                      <FileText className="h-3 w-3 mr-1" />
                      NDA Required
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">{listing.title}</h1>
                {listing.teaser && (
                  <p className="text-lg text-muted-foreground">{listing.teaser}</p>
                )}
              </div>
              <div className="text-left sm:text-right flex flex-col gap-2 items-start sm:items-end">
                <p className="text-2xl sm:text-3xl font-bold text-primary">
                  {listing.price ? `$${listing.price.toLocaleString()}` : 'Price on Application'}
                </p>
                <div className="flex gap-2">
                  {!isOwner && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleFavorite}
                      className="flex items-center gap-2"
                    >
                      <Heart className={`h-4 w-4 ${isFavorited ? 'fill-red-500 text-red-500' : ''}`} />
                      {isFavorited ? 'Saved' : 'Save'}
                    </Button>
                  )}
                  {isOwner && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/broker-portal?section=listings')}>
                      Edit Listing
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="mb-8">
              <div className="aspect-video w-full overflow-hidden rounded-lg bg-muted">
                <img
                  src={getListingImage(listing as any)}
                  alt={listing.title ?? 'Business image'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            {/* Key Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {listing.location_suburb && listing.location_state && (
                <div className="bg-gradient-card p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Location</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{listing.location_suburb}, {listing.location_state}</p>
                </div>
              )}
              {listing.revenue && (
                <div className="bg-gradient-card p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Revenue</span>
                  </div>
                  <p className="text-sm text-muted-foreground">${listing.revenue.toLocaleString()}</p>
                </div>
              )}
              {listing.ebitda && (
                <div className="bg-gradient-card p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">EBITDA</span>
                  </div>
                  <p className="text-sm text-muted-foreground">${listing.ebitda.toLocaleString()}</p>
                </div>
              )}
              <div className="bg-gradient-card p-4 rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Listed</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {new Date(listing.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* About Section */}
              {listing.description && (
                <Card className="shadow-medium">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Briefcase className="h-5 w-5" />
                      About This Business
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {listing.description}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Financial Information */}
              {(listing.revenue || listing.ebitda) && (
                <Card className="shadow-medium">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Financial Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {listing.revenue && (
                        <div className="p-4 bg-gradient-card rounded-lg border">
                          <p className="text-sm text-muted-foreground mb-1">Annual Revenue</p>
                          <p className="text-xl font-semibold text-primary">
                            ${listing.revenue.toLocaleString()}
                          </p>
                        </div>
                      )}
                      {listing.ebitda && (
                        <div className="p-4 bg-gradient-card rounded-lg border">
                          <p className="text-sm text-muted-foreground mb-1">EBITDA</p>
                          <p className="text-xl font-semibold text-primary">
                            ${listing.ebitda.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Contact Form */}
            <div className="space-y-6">
              {/* Express Interest - Sticky on Desktop */}
              <div className="lg:sticky lg:top-6">
                <Card className="shadow-large border-primary/20">
                  <CardHeader className="text-center">
                    <CardTitle className="text-xl text-primary">Interested in this business?</CardTitle>
                    <CardDescription>
                      Get in touch with the Broker / Seller to learn more
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {listing.requires_nda && !hasSignedNDA && (
                      <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-orange-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-orange-800">NDA Required</p>
                            <p className="text-orange-700">You'll need to sign a Non-Disclosure Agreement before accessing detailed information.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      className="w-full h-12 text-lg font-semibold"
                      size="lg"
                      onClick={handleExpressInterest}
                    >
                      {listing.requires_nda && !hasSignedNDA ? 'Sign NDA & Get in Touch' : 'Get in Touch'}
                    </Button>
                    
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-xl">Express Your Interest</DialogTitle>
                          <DialogDescription className="text-base">
                            Fill out the form below and the broker will contact you within 24 hours.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleLeadSubmit} className="space-y-6 mt-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="name" className="text-sm font-medium">
                                Full Name <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id="name"
                                value={leadForm.name}
                                onChange={(e) => {
                                  setLeadForm(prev => ({ ...prev, name: e.target.value }))
                                  if (errors.name) setErrors(prev => ({ ...prev, name: '' }))
                                }}
                                placeholder="Enter your full name"
                                className={errors.name ? 'border-destructive h-11' : 'h-11'}
                                required
                                maxLength={100}
                              />
                              {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="email" className="text-sm font-medium">
                                Email <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id="email"
                                type="email"
                                value={leadForm.email}
                                onChange={(e) => {
                                  setLeadForm(prev => ({ ...prev, email: e.target.value }))
                                  if (errors.email) setErrors(prev => ({ ...prev, email: '' }))
                                }}
                                placeholder="your@email.com"
                                className={errors.email ? 'border-destructive h-11' : 'h-11'}
                                required
                                maxLength={255}
                              />
                              {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                            <Input
                              id="phone"
                              type="tel"
                              value={leadForm.phone}
                              onChange={(e) => {
                                setLeadForm(prev => ({ ...prev, phone: e.target.value }))
                                if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }))
                              }}
                              placeholder="Enter your phone number"
                              className={errors.phone ? 'border-destructive h-11' : 'h-11'}
                              maxLength={20}
                            />
                            {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="message" className="text-sm font-medium">Message</Label>
                            <Textarea
                              id="message"
                              value={leadForm.message}
                              onChange={(e) => {
                                setLeadForm(prev => ({ ...prev, message: e.target.value }))
                                if (errors.message) setErrors(prev => ({ ...prev, message: '' }))
                              }}
                              placeholder="Tell us about your interest, experience, and any questions you have..."
                              rows={4}
                              className={errors.message ? 'border-destructive resize-none' : 'resize-none'}
                              maxLength={2000}
                            />
                            {errors.message && <p className="text-sm text-destructive mt-1">{errors.message}</p>}
                            <p className="text-xs text-muted-foreground">{leadForm.message.length}/2000 characters</p>
                          </div>
                          <div className="pt-4 border-t">
                            <Button
                              type="submit"
                              className="w-full h-12 text-base font-semibold"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" />
                                  Submitting...
                                </>
                              ) : (
                                'Submit Interest'
                              )}
                            </Button>
                            <p className="text-xs text-muted-foreground text-center mt-3">
                              We'll never share your details with third parties
                            </p>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                    
                    {listing && (
                      <NDAModal
                        isOpen={showNDAModal}
                        onClose={() => setShowNDAModal(false)}
                        onAccept={handleNDAAccepted}
                        listingId={listing.id}
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Business Summary Card */}
                <Card className="shadow-medium mt-6">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Quick Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-sm text-muted-foreground">Sector</span>
                        <Badge variant="outline" className="font-medium">{listing.sector}</Badge>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-sm text-muted-foreground">Location</span>
                        <span className="text-sm font-medium text-right">
                          {listing.location_suburb && listing.location_state
                            ? `${listing.location_suburb}, ${listing.location_state}`
                            : listing.location_state || 'Contact for details'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-sm text-muted-foreground">Status</span>
                        <Badge variant="secondary" className="capitalize">{listing.status}</Badge>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-muted-foreground">Listed</span>
                        <span className="text-sm font-medium">
                          {new Date(listing.created_at).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default BusinessDetail